import * as React from 'react';
import { useState } from 'react';
import { Form, Formik } from 'formik';
import { REPORT_TYPE_SERVER_TO_CLIENT_MAPPING } from '../../../../../../consts/report';
import {
  getReportDefaultGendersForMultiSelect,
  getReportAgesForMultiSelect
} from '../../../../../../helpers/multiselect/multiselect';
import { School } from '../../../../../../models/school';
import { SchoolForm } from '../../../../../../models/form';
import { getStartDateForSchoolYear } from '../../../../../../helpers/calendar/calendar';
import '../SchoolEvents.scss';
import {
  ReportActions,
  ReportCheckboxGrid,
  ReportDateRange,
  ReportDescription,
  ReportSection,
  ReportToggle,
  ReportToggleList
} from '../SchoolEventView/Reports/ReportFormUi';

interface Props {
  onCloseClick: () => void;
  onSubmit: (data: any, reportType: string) => void;
  school: School;
  forms: SchoolForm[];
}

// report 014
export function FixtureResultsReport({ onCloseClick, onSubmit, school, forms }: Props) {
  const now = new Date();
  let dateFrom = getStartDateForSchoolYear(now);
  const genders = getReportDefaultGendersForMultiSelect();
  const ages = getReportAgesForMultiSelect(school, forms);

  const [isAgesAndGendersChecked, setIsAgesAndGendersChecked] = useState(true);

  const requestInitial = {
    dateFrom,
    dateTo: now,
    isShowInterSchool: true,
    isShowHouses: true,
    isShowIntraSchool: true,
    isShowUnclosed: false,
    genders,
    ages,
    isSelectAllAges: true
  };

  const onGendersChange = (index, values, setFieldValue) => {
    const genderItems = values.genders;
    const ageItems = values.ages;

    const genderItemsUpdated = genderItems.map((genderItem, genderIndex) => {
      return genderIndex === index
        ? {
            ...genderItem,
            isChecked: !genderItem.isChecked
          }
        : genderItem;
    });

    const gendersChecked = genderItemsUpdated.map(gender => gender.isChecked);
    const agesChecked = ageItems.map(age => age.isChecked);
    const isCheckedGendersExist = gendersChecked.includes(true);
    const isCheckedAgesExist = agesChecked.includes(true);

    setIsAgesAndGendersChecked(isCheckedGendersExist && isCheckedAgesExist);
    setFieldValue('genders', genderItemsUpdated);
  };

  const onAgesChange = (index, values, setFieldValue) => {
    const ageItems = values.ages;
    const genderItems = values.genders;

    const ageItemsUpdated = ageItems.map((ageItem, ageIndex) => {
      return ageIndex === index
        ? {
            ...ageItem,
            isChecked: !ageItem.isChecked
          }
        : ageItem;
    });

    const gendersChecked = genderItems.map(gender => gender.isChecked);
    const agesChecked = ageItemsUpdated.map(age => age.isChecked);
    const isCheckedGendersExist = gendersChecked.includes(true);
    const isCheckedAgesExist = agesChecked.includes(true);

    const isSelectAllAges = ageItemsUpdated.every(age => age.isChecked === true);

    setIsAgesAndGendersChecked(isCheckedAgesExist && isCheckedGendersExist);
    setFieldValue('isSelectAllAges', isSelectAllAges);
    setFieldValue('ages', ageItemsUpdated);
  };

  const onSelectAllAgesChange = (event, values, setFieldValue) => {
    const isSelectAllAges = event.target.checked;
    const ageItems = values.ages;
    const genderItems = values.genders;

    const ageItemsUpdated = ageItems.map(age => {
      return {
        ...age,
        isChecked: isSelectAllAges
      };
    });

    const gendersChecked = genderItems.map(gender => gender.isChecked);
    const agesChecked = ageItemsUpdated.map(age => age.isChecked);
    const isCheckedGendersExist = gendersChecked.includes(true);
    const isCheckedAgesExist = agesChecked.includes(true);

    setIsAgesAndGendersChecked(isCheckedAgesExist && isCheckedGendersExist);
    setFieldValue('isSelectAllAges', isSelectAllAges);
    setFieldValue('ages', ageItemsUpdated);
  };

  return (
    <Formik
      onSubmit={values => onSubmit(values, REPORT_TYPE_SERVER_TO_CLIENT_MAPPING.FIXTURE_RESULTS)}
      initialValues={requestInitial}
    >
      {({ values, setFieldValue }) => (
        <Form className="eReportForm">
          <ReportDescription>
            Create a results report for team fixtures matching the period and audience criteria below.
          </ReportDescription>
          <ReportSection
            number={1}
            title="Reporting period"
            hint="Choose the start and end dates included in this report."
          >
            <ReportDateRange
              dateFrom={values.dateFrom}
              dateTo={values.dateTo}
              onChangeFrom={date => {
                setFieldValue('dateFrom', date);
                if (values.dateTo < date) setFieldValue('dateTo', date);
              }}
              onChangeTo={date => setFieldValue('dateTo', date)}
            />
          </ReportSection>
          <ReportSection
            number={2}
            title="Fixture types"
            hint="Choose which categories of fixtures and results should be included."
          >
            <ReportToggleList>
              <ReportToggle
                name="isShowInterSchool"
                label="Inter-school fixtures"
                hint="Fixtures played against another school."
                value={values.isShowInterSchool}
                onChange={value => setFieldValue('isShowInterSchool', value)}
              />
              <ReportToggle
                name="isShowHouses"
                label="House fixtures"
                hint="Internal fixtures between houses."
                value={values.isShowHouses}
                onChange={value => setFieldValue('isShowHouses', value)}
              />
              <ReportToggle
                name="isShowIntraSchool"
                label="Internal events"
                hint="Other internal school matches."
                value={values.isShowIntraSchool}
                onChange={value => setFieldValue('isShowIntraSchool', value)}
              />
              <ReportToggle
                name="isShowUnclosed"
                label="Events awaiting results"
                hint="Include events that have not yet been closed."
                value={values.isShowUnclosed}
                onChange={value => setFieldValue('isShowUnclosed', value)}
              />
            </ReportToggleList>
          </ReportSection>
          <ReportSection number={3} title="Gender" hint="Choose the student genders included in the report.">
            <ReportCheckboxGrid
              items={values.genders}
              onChange={index => onGendersChange(index, values, setFieldValue)}
            />
          </ReportSection>
          <ReportSection number={4} title="Age groups" hint="Choose one or more age groups.">
            <ReportToggle
              name="isSelectAllAges"
              label="Select all ages"
              hint="Include every available age group."
              value={values.isSelectAllAges}
              onChange={value => onSelectAllAgesChange({ target: { checked: value } }, values, setFieldValue)}
            />
            <ReportCheckboxGrid items={values.ages} onChange={index => onAgesChange(index, values, setFieldValue)} />
          </ReportSection>
          <ReportActions onCancel={onCloseClick} disabled={!isAgesAndGendersChecked} />
        </Form>
      )}
    </Formik>
  );
}
